import type { Activity, ActivityType } from 'types';
import { applicableFactorPolicy } from '../factorPolicy/form';

export type DiscountContext = 'standard_discount' | 'non_standard_discount' | 'package_discount';
export const discountContexts: readonly DiscountContext[] = [
  'standard_discount',
  'non_standard_discount',
  'package_discount',
];

export function applicableDiscountContext(
  context: DiscountContext,
  type: ActivityType,
  activity: Activity | null,
  productKind: Activity['kind'] = 'MEDIA',
): boolean {
  if (activity && activity.kind !== productKind) return false;
  if (productKind === 'SERVICE') return context === 'standard_discount';
  if (context === 'package_discount') return type === 'RADIO' && !activity?.non_standard;
  if (!activity) return true;
  return context === 'non_standard_discount' ? activity.non_standard : !activity.non_standard;
}

export type RuleValueSide = {
  side: 'client' | 'provider';
  discountField: (context: DiscountContext) => string;
  policyField: 'client_factor_policy' | 'provider_factor_policy' | 'factor_policy';
};

export function ruleValueFields(
  type: ActivityType,
  activity: Activity | null,
  sides: readonly RuleValueSide[],
  productKind: Activity['kind'] = 'MEDIA',
) {
  return [
    ...discountContexts.flatMap((context) =>
      sides.map((side) => ({
        name: side.discountField(context),
        context: context as DiscountContext | null,
        side,
        applicable: applicableDiscountContext(context, type, activity, productKind),
      })),
    ),
    ...sides.map((side) => ({
      name: side.policyField,
      context: null,
      side,
      applicable: productKind === 'MEDIA' && applicableFactorPolicy(type, activity),
    })),
  ];
}

export function clearRuleValues<Values extends Record<string, unknown>>(
  values: Values,
  fields: ReturnType<typeof ruleValueFields>,
): Values {
  return {
    ...values,
    ...Object.fromEntries(fields.filter((field) => !field.applicable).map(({ name }) => [name, null])),
  };
}

export function validateRuleValues(
  values: Record<string, unknown>,
  fields: ReturnType<typeof ruleValueFields>,
  allowEmpty = false,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (values[field.name] != null && !field.applicable)
      errors[field.name] = field.context
        ? 'Clear this value or change the activity/type.'
        : 'Policies require Radio media with standard activities.';
  }
  const firstEnabled = fields.find((field) => field.context && field.applicable);
  if (!allowEmpty && firstEnabled && !fields.some(({ name }) => values[name] != null)) {
    errors[firstEnabled.name] = fields.some((field) => !field.context && field.applicable)
      ? 'Enter a discount or define an auto factor policy.'
      : `Enter at least one ${fields.filter((field) => !field.context).length === 2 ? 'client or provider ' : ''}discount. Zero is allowed.`;
  }
  return errors;
}
